import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { BillingService } from '../services';
import type { AddCardRequest } from '../types';

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof AxiosError && error.response?.data) {
    const data = error.response.data as Record<string, unknown>;
    if (typeof data.message === 'string') return data.message;
    if (typeof data.error === 'string') return data.error;
  }
  if (error instanceof Error) return error.message;
  return fallback;
};

/**
 * Hook para gerenciar cartões de crédito da empresa
 */
export const useCards = (enabled: boolean = true) => {
  const queryClient = useQueryClient();

  // Get all cards
  const {
    data: cards,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['cards'],
    queryFn: ({ signal }) => BillingService.getCards(signal),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled,
  });

  // Add card
  const addMutation = useMutation({
    mutationFn: (data: AddCardRequest) => BillingService.addCard(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      toast.success('Cartão adicionado com sucesso!');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Erro ao adicionar cartão'));
    },
  });

  // Delete card
  const deleteMutation = useMutation({
    mutationFn: (index: number) => BillingService.deleteCard(index),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      toast.success('Cartão removido com sucesso');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Erro ao remover cartão'));
    },
  });

  // Set default card
  const setDefaultMutation = useMutation({
    mutationFn: (index: number) => BillingService.setDefaultCard(index),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      toast.success('Cartão padrão atualizado');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Erro ao definir cartão padrão'));
    },
  });

  return {
    cards,
    isLoading,
    error,
    refetch,
    addCard: addMutation.mutate,
    addCardAsync: addMutation.mutateAsync,
    isAdding: addMutation.isPending,
    deleteCard: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    setDefaultCard: setDefaultMutation.mutate,
    isSettingDefault: setDefaultMutation.isPending,
  };
};
